
import React, { useState, useRef, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Loader2, Edit2, Check, X } from 'lucide-react';

interface EditableFieldProps {
  value: string | number | null | undefined;
  onSave: (value: string) => Promise<boolean>;
  label: string;
  type?: 'text' | 'number' | 'textarea' | 'date';
  placeholder?: string;
  disabled?: boolean;
  multiline?: boolean;
  validator?: (value: string) => string | null; // Возвращает ошибку или null
}

export const EditableField: React.FC<EditableFieldProps> = ({
  value,
  onSave,
  label,
  type = 'text',
  placeholder,
  disabled = false,
  multiline = false,
  validator
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(value?.toString() || '');
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isEditing]);

  const handleEdit = () => {
    setEditValue(value?.toString() || '');
    setError(null);
    setIsEditing(true);
  };

  const handleCancel = () => {
    setEditValue(value?.toString() || '');
    setError(null);
    setIsEditing(false);
  };

  const handleSave = async () => {
    // Валидация
    if (validator) {
      const validationError = validator(editValue);
      if (validationError) {
        setError(validationError);
        return;
      }
    }

    setIsSaving(true);
    setError(null);

    try {
      const success = await onSave(editValue);
      if (success) {
        setIsEditing(false);
      } else {
        setError('Не удалось сохранить изменения');
      }
    } catch (err) {
      setError('Произошла ошибка при сохранении');
    } finally {
      setIsSaving(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !multiline) {
      handleSave();
    } else if (e.key === 'Escape') {
      handleCancel();
    }
  };

  if (disabled) {
    return (
      <div className="flex items-center gap-2">
        <span className="text-sm">{value || '-'}</span>
      </div>
    );
  }

  if (isEditing) {
    const InputComponent = multiline ? Textarea : Input;
    
    return (
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <InputComponent
            ref={inputRef as any}
            type={type}
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            className={error ? 'border-red-500' : ''}
            disabled={isSaving}
          />
          <Button
            size="sm"
            variant="ghost"
            onClick={handleSave}
            disabled={isSaving}
          >
            {isSaving ? (
              <Loader2 className="h-3 w-3 animate-spin" />
            ) : (
              <Check className="h-3 w-3" />
            )}
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={handleCancel}
            disabled={isSaving}
          >
            <X className="h-3 w-3" />
          </Button>
        </div>
        {error && (
          <p className="text-sm text-red-500">{error}</p>
        )}
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2 group">
      <span className="text-sm">{value || '-'}</span>
      <Button
        size="sm"
        variant="ghost"
        className="opacity-0 group-hover:opacity-100 transition-opacity"
        onClick={handleEdit}
      >
        <Edit2 className="h-3 w-3" />
      </Button>
    </div>
  );
};
